<template>
  <ul>
    <v-sheet>
      <v-text-field
        v-model.trim="newToken.label"
        placeholder="Label"
        icon="label"
      />

      <v-btn prepend-icon="mdi-plus" @click="addToken()"> Add Token </v-btn>
      <v-alert v-if="error" type="error" has-icon>
        {{ errorMessage }}
      </v-alert>
    </v-sheet>
    <div class="control">
      <v-switch v-model="showRevoked" label="Show revoked" />
    </div>
    <!-- TODO REPLACE b-table -->
    <v-data-table-server
      :data="visibleTokens"
      :loading="tokens.status.value.fetching"
      :total="tokens.info.size"
      :per-page="tokens.pagination.pageSize"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      :row-class="(row, index) => (row.enabled === false ? 'is-invalid' : '')"
      striped
      paginated
      backend-pagination
      @page-change="onPageChange"
    >
      <template #item.label="props">
        {{ props.row.label }}
      </template>
      <template #item.hash="props">
        <code class="has-text-grey">{{ props.row.truncated_key_hash }}</code>
      </template>
      <template #item.enabled="props">
        <v-btn
          v-if="props.row.enabled"
          icon="mdi-close"
          class="button"
          @click="revokeToken(props.row)"
        />
        <code v-else class="has-text-grey">Revoked</code>
      </template>
      <template #no-data>
        <section class="section">
          <div class="content has-text-grey has-text-centered">
            <p>
              <v-icon icon="cancel" size="large" />
            </p>
            <p>Nothing here.</p>
          </div>
        </section>
      </template>
    </v-data-table-server>
    <div :class="{ 'is-active': showNewTokenKey }" class="modal">
      <div class="modal-background" />
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Token key</p>
          <v-btn
            class="delete"
            aria-label="close"
            @click="closeNewTokenKeyModal()"
          />
        </header>
        <section class="modal-card-body">
          <div class="content">
            This is the key for your new token. Please save it securely.
            <blockquote>
              <code>{{ newTokenKey.key }}</code>
            </blockquote>
            The public identifier for you key is:
            <blockquote>
              <code>{{ newTokenKey.hash }}</code>
            </blockquote>
          </div>
        </section>
      </div>
    </div>
  </ul>
</template>

<script setup lang="ts">
import { computed, Ref, ref } from 'vue'
import { useSnackbar } from 'vuetify-use-dialog'

import { apiClient } from '@/api/ApiClient'
import { apiClientV2 } from '@/api/ApiClientV2'
import { TOKEN_DEFAULT } from '@/mock/tokens'
import { Token } from '@/models/core/models'

import { revokeToken } from './revokeToken'
const createSnackbar = useSnackbar()

type TokenListProps = {
  user: string
}
const props = defineProps<TokenListProps>()

const tokens = apiClientV2.subscribe<Token, Token>(
  Token,
  {
    profile: props.user,
  },
  {}
)

const sortField: string = 'enabled'
const sortOrder: string = 'desc'
const defaultSortOrder: string = 'desc'
const showRevoked = ref(false)
const newToken: Ref<Token> = ref(JSON.parse(JSON.stringify(TOKEN_DEFAULT)))
const showNewTokenKey = ref(false)
const errorMessage = ref('')
const error = ref(false)

const newTokenKey = ref({
  key: '',
  hash: '',
})

function onPageChange(page) {
  tokens.setPagination(page)
}

function saveToken(token: Token, value) {
  token.label = value
  apiClient.update('token', token).then((response) => {
    createSnackbar({
      text: 'Successfully saved!',
      snackbarProps: { color: 'green-darken-3' },
    })
  })
}

function addToken() {
  newToken.value.profile = props.user
  apiClient
    .create('token', newToken.value)
    .then((response) => {
      newTokenKey.value.key = response.data.key
      newTokenKey.value.hash = response.data.truncated_key_hash
      showNewTokenKey.value = true
      newToken.value = JSON.parse(JSON.stringify(TOKEN_DEFAULT))
    })
    .catch((error) => {
      errorMessage.value = error.response.data
      error.value = true
    })
}

function closeNewTokenKeyModal() {
  newTokenKey.value.key = ''
  newTokenKey.value.hash = ''
  showNewTokenKey.value = false
}

const visibleTokens = computed(() => {
  if (!showRevoked.value) {
    const t = []
    tokens.objects.forEach((token) => {
      if (token.enabled) {
        t.push(token)
      }
    })
    return t
  } else {
    return tokens.objects
  }
})
</script>

<style lang="scss">
tr.is-invalid {
  background: hsla(195, 3%, 25%, 0.27) !important;
}
</style>
